import WarningIcon from "@/Components/MagicLink/WarningIcon";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Fade,
    IconButton,
    Modal,
    Typography,
    useTheme,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { RequestMasterTypeConfigEntry } from "@/Config/RequestMasterTypeConfig";
import { useEffect, useRef } from "react";

const juliFontFeatureSettings = { fontFeatureSettings: "'liga' off, 'clig' off" } as const;

const modalHeadlineSx: SxProps<Theme> = {
    color: "#000",
    textAlign: "center",
    ...juliFontFeatureSettings,
    fontFamily: '"Juli Sans", Juli, sans-serif',
    fontSize: "22px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "120%",
};

const modalDescriptionSx: SxProps<Theme> = {
    color: "#4D4D4D",
    textAlign: "center",
    ...juliFontFeatureSettings,
    fontFamily: '"Juli Sans", Juli, sans-serif',
    fontSize: "15px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "148%",
};

export type PublicRequestConfirmModalProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isConfirming?: boolean;
    masterTypeConfig: RequestMasterTypeConfigEntry | undefined;
};

export const PublicRequestConfirmModal = ({
    open,
    onClose,
    onConfirm,
    isConfirming = false,
    masterTypeConfig
}: PublicRequestConfirmModalProps) => {
    const theme = useTheme();

    const actionTitle = masterTypeConfig!.actionTitle;
    const confirmMessage = masterTypeConfig!.modalConfirm;

    const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => {
                confirmButtonRef.current?.focus();
            });
        }
    }, [open]);


    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            slotProps={{
                backdrop: {
                    timeout: 250,
                    sx: {
                        backgroundColor: "rgba(0, 0, 0, 0.32)",
                    },
                },
            }}
            sx={{
                zIndex: theme.zIndex.drawer * 7,
                "&:focus": { outline: "none" },
                "& *:focus-visible": { outline: "none" },
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "calc(100% - 32px)",
                        maxWidth: 424,
                        bgcolor: theme.palette.background.default,
                        borderRadius: "7px",
                        padding: "32px",
                        boxSizing: "border-box",
                        outline: "none",
                    }}
                >
                    <IconButton
                        type="button"
                        aria-label="Zavrieť"
                        onClick={onClose}
                        disabled={isConfirming}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "#000",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "32px",
                            width: "100%",
                        }}
                    >
                        <WarningIcon width={64} height={64} />

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "12px",
                                width: "100%",
                            }}
                        >
                            <Typography component="h2" sx={modalHeadlineSx}>
                                {confirmMessage.headline}
                            </Typography>
                            <Typography component="p" sx={modalDescriptionSx}>
                                {confirmMessage.description}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "10px",
                                width: "100%",
                            }}
                        >
                            <Button
                                type="button"
                                variant="outlined"
                                color="error"
                                onClick={onClose}
                                disabled={isConfirming}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    height: 48,
                                    minHeight: 48,
                                    textTransform: "none",
                                    fontWeight: 500,
                                }}
                            >
                                Zavrieť
                            </Button>
                            <LoadingButton
                                ref={confirmButtonRef}
                                type="button"
                                variant="contained"
                                color="error"
                                onClick={onConfirm}
                                loading={isConfirming}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    height: 48,
                                    minHeight: 48,
                                    textTransform: "none",
                                    fontWeight: 500,
                                }}
                            >
                                {actionTitle}
                            </LoadingButton>
                        </Box>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};
